import { TransactionColumn } from "@common/types/views/transactionViews";
import { ViewType } from "@common/types/views/views";
import { MoneyDirection } from "@common/constants";
import { FinancialAccount } from "@common/types/financialAccount";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { parseDateOnlyAsUTC } from "@common/helpers/date";
import { earliestAllowedMatchDateOnly } from "@common/helpers/reconciliation";
import { addDays, max, subDays } from "date-fns";

interface BuildMatchSearchViewParams {
    financialAccount: Pick<FinancialAccount, "id" | "entityId">;
    amount?: number;
    openingDate: string;
    closingDate: string;
    additionalDays: number;
    previousClosingDate?: string;
    lockDateOnly?: string;
    notBeforeDateOnly?: string;
    excludedTransactionIds?: number[];
    labelGetter: (transaction: TransactionListItem) => string;
    isDisabled: (transaction: TransactionListItem) => boolean;
    tooltipGetter: (transaction: TransactionListItem) => string | undefined;
    onCTAClick: (transaction: TransactionListItem) => void;
}

type GetSearchStartParams = Pick<
    BuildMatchSearchViewParams,
    | "openingDate"
    | "additionalDays"
    | "previousClosingDate"
    | "lockDateOnly"
    | "notBeforeDateOnly"
>;

function getSearchStart({
    openingDate,
    additionalDays,
    previousClosingDate,
    lockDateOnly,
    notBeforeDateOnly,
}: GetSearchStartParams): Date {
    const allowedStarts = [
        earliestAllowedMatchDateOnly({ previousClosingDate, lockDateOnly }),
        notBeforeDateOnly,
    ].flatMap((dateOnly) => (dateOnly ? [parseDateOnlyAsUTC(dateOnly)] : []));
    return max([subDays(openingDate, additionalDays + 1), ...allowedStarts]);
}

function directionForAmount(amount?: number) {
    if (amount == null || amount === 0) {
        return undefined;
    }
    return amount < 0 ? MoneyDirection.MONEY_OUT : MoneyDirection.MONEY_IN;
}

export const buildMatchSearchView = ({
    financialAccount,
    amount,
    openingDate,
    closingDate,
    additionalDays,
    previousClosingDate,
    lockDateOnly,
    notBeforeDateOnly,
    excludedTransactionIds = [],
    labelGetter,
    isDisabled,
    tooltipGetter,
    onCTAClick,
}: BuildMatchSearchViewParams) => ({
    type: ViewType.TRANSACTION as const,
    columns: [
        { id: TransactionColumn.DATE as const },
        { id: TransactionColumn.LOCK as const },
        {
            id: TransactionColumn.BANK_DESCRIPTION as const,
            options: {
                minSize: 600,
                maxSize: 600,
            },
        },
        { id: TransactionColumn.ORIGINAL_AMOUNT as const },
        { id: TransactionColumn.ACCOUNT as const },
        {
            id: TransactionColumn.CTA as const,
            options: {
                label: labelGetter,
                isDisabled,
                tooltip: tooltipGetter,
                onClick: onCTAClick,
            },
            pin: "right",
        },
    ],
    filters: {
        entityIds:
            financialAccount.entityId === null
                ? []
                : [financialAccount.entityId],
        accountIds: [financialAccount.id],
        minAmount: amount,
        maxAmount: amount,
        direction: directionForAmount(amount),
        excludeSplits: true,
        start: getSearchStart({
            openingDate,
            additionalDays,
            previousClosingDate,
            lockDateOnly,
            notBeforeDateOnly,
        }),
        end: addDays(closingDate, additionalDays + 1),
        ...(excludedTransactionIds.length > 0
            ? { excludedTransactionIds }
            : {}),
    },
});
